import { useMemo, useState, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Loader2, Plus, ChevronRight } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCustomer, useCustomerRelated } from "@/hooks/use-api";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { PropertyHero } from "@/components/v2/property-hero";
import { DataTable } from "@/components/v2/data-table";
import { EmptyState } from "@/components/v2/empty-state";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

// Lifecycle status enum mirrors packages/types/src/schemas/contact.schema.ts:95
const LIFECYCLE_LABELS: Record<string, string> = {
  lead: "Lead",
  prospect: "Prospect",
  customer: "Customer",
  former_customer: "Former Customer",
  inactive: "Inactive",
};

const LIFECYCLE_COLORS: Record<string, string> = {
  lead: "var(--color-text-lo)",
  prospect: "var(--color-warning)",
  customer: "var(--color-success)",
  former_customer: "var(--color-text-lo)",
  inactive: "var(--color-text-lo)",
};

// Closed opportunity stages — mirrors ADV-808 closed_won/closed_lost terminal states.
// Treated as not-open for the openOpportunities filter.
const CLOSED_OPPORTUNITY_STAGES = new Set([
  "closed_won",
  "closed_lost",
  "sold",
]);

function getLifecycleLabel(lifecycle: string): string {
  return LIFECYCLE_LABELS[lifecycle] ?? lifecycle;
}

function formatCustomerType(type: string | null | undefined): string | null {
  if (!type) return null;
  return type.replace(/^./, (c) => c.toUpperCase());
}

// Right rail key/value row — skips render when value is falsy. Mirrors ADV-808.
function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

// Right rail section wrapper. Mirrors ADV-808.
function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

// Records tab section wrapper (H2 + count chip + DataTable). Mirrors ADV-808
// pattern of inlining one-off helpers within the detail file.
function RecordsSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <div>
      <h2 className="typo-body font-semibold text-[color:var(--color-text-hi)] mb-3">
        {title}
        {count > 0 && (
          <span className="ml-2 typo-label text-[color:var(--color-text-lo)]">
            {count}
          </span>
        )}
      </h2>
      {children}
    </div>
  );
}

interface CurrentFocus {
  heading: string;
  body: string;
  action: string;
}

function pickCurrentFocus(
  lifecycle: string,
  openOpportunitiesCount: number,
): CurrentFocus {
  if (lifecycle === "lead") {
    return {
      heading: "New lead",
      body: "Schedule an appointment to qualify this lead.",
      action: "Schedule Appointment",
    };
  }
  if (openOpportunitiesCount > 0) {
    const noun = openOpportunitiesCount === 1 ? "opportunity" : "opportunities";
    return {
      heading: "Open opportunities pending",
      body: `Follow up on ${openOpportunitiesCount} active ${noun}.`,
      action: "View Opportunities",
    };
  }
  if (lifecycle === "customer") {
    return {
      heading: "Active customer",
      body: "No open opportunities. Reach out to identify the next deal.",
      action: "New Opportunity",
    };
  }
  if (lifecycle === "former_customer" || lifecycle === "inactive") {
    return {
      heading: "Inactive customer",
      body: "Re-engage to revive the relationship.",
      action: "Reactivate",
    };
  }
  return {
    heading: "No current focus",
    body: "Add a note or schedule an appointment to capture next steps.",
    action: "Add Note",
  };
}

const opportunityColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "deal_number",
    header: "Opportunity #",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="font-medium">
        {(row.original.deal_number as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "stage",
    header: "Stage",
    cell: ({ row }: { row: { original: Row } }) => (
      <StatusBadge status={row.original.stage as string} />
    ),
  },
  {
    accessorKey: "deal_type",
    header: "Type",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="capitalize">
        {((row.original.deal_type as string) || "").replace(/_/g, " ")}
      </span>
    ),
  },
  {
    accessorKey: "proposal_value",
    header: "Value",
    meta: { numeric: true },
    cell: ({ row }: { row: { original: Row } }) =>
      formatCurrency(row.original.proposal_value as number),
  },
  {
    accessorKey: "lead_date",
    header: "Lead Date",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.lead_date as string)}
      </span>
    ),
  },
];

const proposalColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "proposal_number",
    header: "Proposal #",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="font-medium">
        {(row.original.proposal_number as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }: { row: { original: Row } }) => (
      <StatusBadge status={row.original.status as string} />
    ),
  },
  {
    accessorKey: "final_price",
    header: "Amount",
    meta: { numeric: true },
    cell: ({ row }: { row: { original: Row } }) =>
      formatCurrency(row.original.final_price as number),
  },
  {
    accessorKey: "submit_date",
    header: "Date",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.submit_date as string)}
      </span>
    ),
  },
];

const jobColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "job_number",
    header: "Job #",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="font-medium">
        {(row.original.job_number as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }: { row: { original: Row } }) => (
      <StatusBadge status={row.original.status as string} />
    ),
  },
  {
    accessorKey: "scheduled_date",
    header: "Scheduled",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.scheduled_date as string)}
      </span>
    ),
  },
  {
    accessorKey: "total_revenue",
    header: "Revenue",
    meta: { numeric: true },
    cell: ({ row }: { row: { original: Row } }) =>
      formatCurrency(row.original.total_revenue as number),
  },
];

const invoiceColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "invoice_number",
    header: "Invoice #",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="font-medium">
        {(row.original.invoice_number as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }: { row: { original: Row } }) => (
      <StatusBadge status={row.original.status as string} />
    ),
  },
  {
    accessorKey: "total_amount",
    header: "Total",
    meta: { numeric: true },
    cell: ({ row }: { row: { original: Row } }) =>
      formatCurrency(row.original.total_amount as number),
  },
  {
    accessorKey: "balance_due",
    header: "Balance",
    meta: { numeric: true },
    cell: ({ row }: { row: { original: Row } }) =>
      formatCurrency(row.original.balance_due as number),
  },
  {
    accessorKey: "due_date",
    header: "Due",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.due_date as string)}
      </span>
    ),
  },
];

const locationColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "formatted_address",
    header: "Address",
    cell: ({ row }: { row: { original: Row } }) => {
      const formatted = row.original.formatted_address as string | null;
      const street = row.original.street_line_1 as string | null;
      return (
        <span className="font-medium truncate">
          {formatted ?? street ?? "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "city",
    header: "City",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.city as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "state",
    header: "State",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.state as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "location_type",
    header: "Type",
    cell: ({ row }: { row: { original: Row } }) => (
      <span className="capitalize text-[color:var(--color-text-md)]">
        {((row.original.location_type as string) || "").replace(/_/g, " ") ||
          "—"}
      </span>
    ),
  },
];

type TabValue = "overview" | "activity" | "records";

export default function V2CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabValue>("overview");

  const { data, isLoading, isError } = useCustomer(id!);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customer = (data as any)?.customer as Row | undefined;
  const contactRoleId = customer?.contact_role_id as string | undefined;

  const { data: relatedData, isLoading: relatedLoading } =
    useCustomerRelated(contactRoleId);

  const opportunities = (relatedData?.opportunities ?? []) as Row[];
  const proposals = (relatedData?.proposals ?? []) as Row[];
  const jobs = (relatedData?.jobs ?? []) as Row[];
  const invoices = (relatedData?.invoices ?? []) as Row[];
  const locations = (relatedData?.locations ?? []) as Row[];
  const totalRecords =
    opportunities.length +
    proposals.length +
    jobs.length +
    invoices.length +
    locations.length;

  const name = useMemo(() => {
    if (!customer) return `Customer ${id?.slice(0, 8) ?? ""}`;
    const composed = [customer.first_name, customer.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (composed) return composed;
    return (customer.company_name as string) || "Unnamed customer";
  }, [customer, id]);

  const breadcrumbLabel = useMemo(() => {
    if (!customer) return id ?? "...";
    return (customer.customer_number as string) || name;
  }, [customer, id, name]);

  const firstLocation = locations[0] ?? null;

  const address = useMemo(() => {
    if (!firstLocation) return null;
    const formatted = firstLocation.formatted_address as string | null;
    if (formatted) return formatted;
    const parts = [
      firstLocation.street_line_1,
      firstLocation.city,
      firstLocation.state,
    ].filter(Boolean);
    return parts.length > 0 ? (parts.join(", ") as string) : null;
  }, [firstLocation]);

  const lifecycle = (customer?.lifecycle_status as string) || "customer";

  const openOpportunities = opportunities.filter(
    (o) => !CLOSED_OPPORTUNITY_STAGES.has(o.stage as string),
  );
  const openOpportunitiesCount = openOpportunities.length;
  const currentFocus = pickCurrentFocus(lifecycle, openOpportunitiesCount);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        role="status"
        aria-label="Loading customer"
      >
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (isError || (!isLoading && !customer)) {
    const errorTitle = isError
      ? "Failed to load customer"
      : "Customer not found";
    const errorDescription = isError
      ? "Something went wrong. Please try again."
      : "This customer may have been deleted.";
    return (
      <>
        <Breadcrumb className="mb-4 typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/customers"
                className="text-[color:var(--color-text-lo)]"
              >
                Customers
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{id ?? "..."}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <EmptyState title={errorTitle} description={errorDescription} />
      </>
    );
  }

  const previewOpps = openOpportunities.slice(0, 5);

  return (
    <div className="flex flex-col">
      {/* Breadcrumbs + per-page actions cluster */}
      <div className="mb-3 flex items-center justify-between">
        <Breadcrumb className="typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/customers"
                className="text-[color:var(--color-text-lo)]"
              >
                Customers
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{breadcrumbLabel}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2">
          <DetailPageActions entityLabel="Customer" />
        </div>
      </div>

      {/* 3-tab shell */}
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as TabValue)}
        className="flex flex-col"
      >
        <TabsList className="h-auto bg-transparent p-0 border-b border-[color:var(--color-border)] rounded-none w-full justify-start gap-0 mb-0">
          <TabsTrigger
            value="overview"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Activity
          </TabsTrigger>
          <TabsTrigger
            value="records"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Records
            {totalRecords > 0 && (
              <Badge
                variant="secondary"
                className="ml-1.5 h-5 min-w-[20px] px-1.5 text-[11px]"
              >
                {String(totalRecords)}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="mt-0">
          <div className="flex gap-6 pt-6">
            {/* Main column */}
            <div className="flex-1 min-w-0 space-y-5">
              <PropertyHero
                address={address ?? ""}
                lat={(firstLocation?.latitude as number | null) ?? undefined}
                lon={(firstLocation?.longitude as number | null) ?? undefined}
              />

              {/* Title block */}
              <div>
                <h1 className="typo-heading text-[color:var(--color-text-hi)]">
                  {name}
                </h1>
                {customer?.company_name != null &&
                  customer.company_name !== name && (
                    <p className="mt-0.5 typo-body text-[color:var(--color-text-md)]">
                      {customer.company_name as string}
                    </p>
                  )}
              </div>

              {/* Inline properties strip */}
              <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
                <span
                  className="inline-flex items-center rounded-sm border bg-transparent px-1.5 py-0.5 typo-label"
                  style={{
                    borderColor:
                      LIFECYCLE_COLORS[lifecycle] ?? "var(--color-border)",
                    color:
                      LIFECYCLE_COLORS[lifecycle] ?? "var(--color-text-md)",
                  }}
                >
                  {getLifecycleLabel(lifecycle)}
                </span>
                {customer?.email != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span className="truncate">{customer.email as string}</span>
                  </>
                )}
                {customer?.phone != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span className="tabular-nums">
                      {customer.phone as string}
                    </span>
                  </>
                )}
                {openOpportunitiesCount > 0 && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>
                      {openOpportunitiesCount} open{" "}
                      {openOpportunitiesCount === 1
                        ? "opportunity"
                        : "opportunities"}
                    </span>
                  </>
                )}
                {customer?.created_at != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>
                      Since {formatDate(customer.created_at as string)}
                    </span>
                  </>
                )}
              </div>

              {/* Current Focus card */}
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4">
                <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
                  Current Focus
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="typo-body font-semibold text-[color:var(--color-text-hi)]">
                      {currentFocus.heading}
                    </div>
                    <p className="typo-body text-[color:var(--color-text-md)] mt-0.5">
                      {currentFocus.body}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="shrink-0">
                    {currentFocus.action}
                  </Button>
                </div>
              </div>

              {/* Resources row stub */}
              <div className="flex items-center gap-2">
                <span className="typo-label text-[color:var(--color-text-lo)]">
                  Resources
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 gap-1 text-[color:var(--color-text-lo)] text-xs px-2"
                >
                  <Plus className="h-3 w-3" />
                  Add link or document
                </Button>
              </div>

              {/* Open opportunities preview list */}
              {openOpportunitiesCount > 0 && (
                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                      Open Opportunities
                    </span>
                    {openOpportunitiesCount > previewOpps.length && (
                      <button
                        type="button"
                        onClick={() => setTab("records")}
                        className="typo-label text-[color:var(--color-accent)] hover:underline"
                      >
                        See all {openOpportunitiesCount} →
                      </button>
                    )}
                  </div>
                  <div className="rounded-md border border-[color:var(--color-border)] divide-y divide-[color:var(--color-border)]">
                    {previewOpps.map((opp) => (
                      <button
                        key={(opp.id as string) ?? (opp.deal_number as string)}
                        type="button"
                        onClick={() =>
                          navigate(`/opportunities/${opp.id as string}`)
                        }
                        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[color:var(--color-elevated)]"
                      >
                        <span className="typo-body font-medium text-[color:var(--color-text-hi)] truncate">
                          {(opp.deal_number as string) ||
                            (opp.name as string) ||
                            "Untitled opportunity"}
                        </span>
                        <StatusBadge status={opp.stage as string} />
                        <span className="ml-auto typo-body tabular-nums text-[color:var(--color-text-md)]">
                          {formatCurrency(opp.proposal_value as number)}
                        </span>
                        <ChevronRight className="h-4 w-4 text-[color:var(--color-text-lo)]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right rail */}
            <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
              <RailSection title="Properties">
                <InfoRow label="Status" value={getLifecycleLabel(lifecycle)} />
                <InfoRow
                  label="Customer #"
                  value={customer?.customer_number as string | null}
                />
                <InfoRow
                  label="Type"
                  value={formatCustomerType(
                    customer?.customer_type as string | null,
                  )}
                />
                <InfoRow
                  label="Email"
                  value={customer?.email as string | null}
                />
                <InfoRow
                  label="Phone"
                  value={customer?.phone as string | null}
                />
                <InfoRow
                  label="Lead source"
                  value={customer?.lead_source as string | null}
                />
                <InfoRow
                  label="Addresses"
                  value={locations.length > 0 ? String(locations.length) : null}
                />
                <InfoRow
                  label="Open opportunities"
                  value={
                    openOpportunitiesCount > 0
                      ? String(openOpportunitiesCount)
                      : null
                  }
                />
                <InfoRow
                  label="Created"
                  value={
                    customer?.created_at
                      ? formatDate(customer.created_at as string)
                      : null
                  }
                />
              </RailSection>

              <RailSection title="Activity">
                <EntityActivityPreview
                  entityType="customer"
                  entityId={id!}
                  onSeeAll={() => setTab("activity")}
                />
              </RailSection>
            </div>
          </div>
        </TabsContent>

        {/* ── Activity ── */}
        <TabsContent value="activity" className="mt-0 pt-6">
          <EntityActivityFeed
            entityType="customer"
            entityId={id!}
            emptyDescription="All messages, calls, and notes for this customer appear here once the first contact is logged."
          />
        </TabsContent>

        {/* ── Records ── */}
        <TabsContent value="records" className="mt-0 pt-6 space-y-8">
          <RecordsSection title="Opportunities" count={opportunities.length}>
            <DataTable
              columns={opportunityColumns}
              data={opportunities}
              isLoading={relatedLoading}
              onRowClick={(row) =>
                navigate(`/opportunities/${row.id as string}`)
              }
              emptyMessage="No opportunities yet"
              emptyDescription="Opportunities for this customer will appear here."
            />
          </RecordsSection>

          <RecordsSection title="Proposals" count={proposals.length}>
            <DataTable
              columns={proposalColumns}
              data={proposals}
              isLoading={relatedLoading}
              onRowClick={(row) => navigate(`/proposals/${row.id as string}`)}
              emptyMessage="No proposals yet"
              emptyDescription="Proposals for this customer will appear here."
            />
          </RecordsSection>

          <RecordsSection title="Jobs" count={jobs.length}>
            <DataTable
              columns={jobColumns}
              data={jobs}
              isLoading={relatedLoading}
              onRowClick={(row) => navigate(`/jobs/${row.id as string}`)}
              emptyMessage="No jobs yet"
              emptyDescription="Jobs for this customer will appear here."
            />
          </RecordsSection>

          <RecordsSection title="Invoices" count={invoices.length}>
            <DataTable
              columns={invoiceColumns}
              data={invoices}
              isLoading={relatedLoading}
              onRowClick={(row) => navigate(`/invoices/${row.id as string}`)}
              emptyMessage="No invoices yet"
              emptyDescription="Invoices for this customer will appear here."
            />
          </RecordsSection>

          <RecordsSection title="Properties" count={locations.length}>
            <DataTable
              columns={locationColumns}
              data={locations}
              isLoading={relatedLoading}
              onRowClick={(row) => navigate(`/locations/${row.id as string}`)}
              emptyMessage="No properties yet"
              emptyDescription="Properties associated with this customer will appear here."
            />
          </RecordsSection>
        </TabsContent>
      </Tabs>
    </div>
  );
}
